// import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps, GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Grid from "@insite/mobius/Icons/Grid";
import { GridContainer, GridItem } from "@insite/mobius/index";
import LazyImage, { LazyImageStyle } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import ListItem, { OrderedList, UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse, { domToReact, HTMLReactParserOptions } from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const parserOptions: HTMLReactParserOptions = {
    replace: node => {
        const { name, children, attribs = { style: "" }, ...rest } = node;
        const { style: pStyle, class: className, ...otherAttribs } = attribs;

        const style = pStyle ? pStyle.replace(/(\d+)pt/g, "$1px") : pStyle;

        otherAttribs.className = className;

        if (name && ["h1", "h2", "h3", "h4", "h5", "h6", "body", "p", "legend"].includes(name) && children) {
            return (
                <Typography css={style as any} {...otherAttribs} variant={name as TypographyProps["variant"]}>
                    {children && domToReact(children, parserOptions)}
                </Typography>
            );
        }
        if (name === "ul") {
            return (
                <UnorderedList css={style as any} {...otherAttribs}>
                    {children && domToReact(children, parserOptions)}
                </UnorderedList>
            );
        }
        if (name === "ol") {
            return (
                <OrderedList css={style as any} {...otherAttribs}>
                    {children && domToReact(children, parserOptions)}
                </OrderedList>
            );
        }
        if (name === "li") {
            return (
                <ListItem css={style as any} {...otherAttribs}>
                    {children && domToReact(children, parserOptions)}
                </ListItem>
            );
        }
        if (name === "img") {
            const { alt, ...remainingAttribs } = otherAttribs;
            return <LazyImage as="span" altText={alt} css={style as any} {...remainingAttribs} />;
        }
        if (name === "a") {
            return (
                <Link css={style as any} id={otherAttribs.name} {...otherAttribs}>
                    {children && domToReact(children, parserOptions)}
                </Link>
            );
        }
    },
};

const mapStateToProps = (state: ApplicationState) => ({
    page: getCurrentPage(state),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface NewsPageViewStyles {
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    newsWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
    leftGridItem?: GridItemProps;
    rightGridItem?: GridItemProps;
}

export const newsPageViewStyles: NewsPageViewStyles = {
    gridContainer: {
        css: css`
            ${GridWrapper} {
                //  padding: 0 calc(4% - 10px) !important;
                margin-bottom: 70px !important;
            }
        `,
    },
    leftGridItem: {
        width: [12, 12, 7, 7, 8],
        css: css`
            margin-right: auto !important;
        `,
    },
    rightGridItem: {
        width: [12, 12, 5, 5, 4],
        css: css`
            max-width: 360px !important;
            div[class^="Wrapper"] > div[class^="Wrapper"] > ${GridWrapper} {
                div[class^="GridOffset"] {
                    max-width: 100% !important;
                }
            }
        `,
    },
    newsWrapper: {
        css: css`
            span {
                font-size: 1rem !important;
            }
        `,
    },
    contentWrapper: {
        css: css`
            margin-top: 2.5em;
            p {
                font-family: "Roboto-Regular" !important;
            }
            ul {
                li {
                    font-family: "Roboto-Regular" !important;
                }
            }
            a {
                span {
                    color: ${getColor("primary")} !important;
                }
            }
            iframe {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 100% !important;
                        `,
                        css`
                            width: 100% !important;
                        `,
                        css`
                            width: 100% !important;
                        `,
                        css`
                            width: 100% !important;
                        `,
                        null,
                    ])}
            }
            ${LazyImageStyle} {
                margin: 0 14px 20px 0 !important;
                height: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 100% !important;
                        `,
                        css`
                            width: 100% !important;
                        `,
                        css`
                            width: 100% !important;
                        `,
                        css`
                            width: 100% !important;
                        `,
                        null,
                    ])}
            }
        `,
    },
    dateAndAuthorWrapper: {
        css: css`
            display: flex;
            flex-direction: column-reverse;
        `,
    },
    authorText: {
        css: css`
            margin-left: 5px;
            font-weight: 600;
            font-size: 1rem;
        `,
    },
    publishDateText: {
        css: css`
            border-bottom: 2px solid ${getColor("secondary")};
            padding-bottom: 5px;
        `,
    },
};

const styles = newsPageViewStyles;

const NewsPageView = ({ page, id }: Props) => {
    if (!page) {
        return null;
    }

    return (
        <GridContainer
            key={page.id}
            css={css`
                // padding: 0 calc(4% - 10px);
                margin-bottom: 70px;
            `}
        >
            <GridItem {...styles.leftGridItem}>
                <StyledWrapper {...styles.newsWrapper}>
                    <StyledWrapper {...styles.dateAndAuthorWrapper}>
                        {page.fields["author"] && (
                            <Typography
                                as="p"
                                css={css`
                                    margin-top: 10px;
                                `}
                            >
                                By
                                <Typography
                                    {...styles.authorText}
                                    data-test-selector="newslist_author"
                                    className="news-author"
                                >
                                    {page.fields["author"]}
                                </Typography>
                            </Typography>
                        )}
                        {page.fields["publishDate"] && (
                            <Typography {...styles.publishDateText} data-test-selector="newslist_publishdate">
                                <LocalizedDateTime
                                    dateTime={new Date(page.fields["publishDate"])}
                                    options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                />
                            </Typography>
                        )}
                    </StyledWrapper>
                    {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                        <StyledWrapper
                            {...styles.contentWrapper}
                            data-test-selector="newslist_content"
                            className="news-page-content"
                        >
                            {parse(page.fields["newsContent"], parserOptions)}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.rightGridItem}>
                <Zone contentId={id} zoneName="SENewsLinksContainer" />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(NewsPageView),
    definition: {
        group: "News Page",
        allowedContexts: ["NewsPage"],
    },
};

export default widgetModule;
